/**
 * Stock Ticker Module
 *
 * Fetches stock data from Eulerpool API and displays it in a ticker bar.
 * Updates every 5 minutes.
 */

const API_KEY =
  "ae10c9097be12d5056cb7de86c7fc54807b5cd8bb738460cc5d1130588f0d7ba";
const API_ENDPOINT = `https://backend.eulerpool.com/api/1/trends/ticker-trends/all?token=${API_KEY}`;
const UPDATE_INTERVAL = 300000; // 5 minutes

// Stocks to display in order
const ALLOWED_SOURCES = [
  "DAX",
  "STOXX50E",
  "S&P500",
  "IXIC",
  "RUT",
  "N225",
  "EURUSD",
  "EURCHF",
];

/**
 * Format a number for display with German locale
 * @param {number|string} number - The number to format
 * @param {string} stockType - The type of stock ('index' or 'forex')
 * @returns {string} Formatted number string
 */
function formatNumber(number, stockType) {
  const decimalPlaces = stockType === "index" ? 2 : 4;
  return parseFloat(number).toLocaleString("de-DE", {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
}

/**
 * Create a stock item element
 * @param {string} key - Stock identifier
 * @param {Object} value - Stock data from API
 * @returns {HTMLLIElement} Stock item element
 */
function createStockItem(key, value) {
  const stockType = value.type;
  let currentRaw, previousRaw;

  if (stockType === "index") {
    currentRaw = value.currentQuote;
    previousRaw = value.previousDayQuote;
  } else if (stockType === "forex") {
    currentRaw = value.rate;
    previousRaw = value.previousDayRate;
  }

  const currentQuote = currentRaw ? formatNumber(currentRaw, stockType) : "N/A";

  const listItem = document.createElement("li");
  listItem.className = "stocks-item";

  // Stock name
  const stockName = document.createElement("span");
  stockName.className = "stocks-item__name";
  stockName.textContent = value.name || key;
  listItem.appendChild(stockName);

  // Stock value
  const stockValue = document.createElement("span");
  stockValue.className = "stocks-item__value";
  stockValue.textContent = currentQuote;
  listItem.appendChild(stockValue);

  // Stock trend
  const stockTrend = document.createElement("span");
  stockTrend.className = "stocks-item__trend";

  if (currentRaw != null && previousRaw != null) {
    if (currentRaw > previousRaw) {
      stockTrend.textContent = "↑";
      stockTrend.classList.add("stocks-item__trend--pos");
    } else if (currentRaw < previousRaw) {
      stockTrend.textContent = "↓";
      stockTrend.classList.add("stocks-item__trend--neg");
    }
  }

  listItem.appendChild(stockTrend);

  return listItem;
}

/**
 * Fetch and update the ticker with stock data
 * @param {HTMLElement} container - The ticker container element
 */
async function updateTicker(container) {
  const tickerBar = container.closest(".stock-ticker");

  try {
    const response = await fetch(API_ENDPOINT);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    // Clear existing ticker contents
    container.innerHTML = "";

    // Add stock items in defined order
    ALLOWED_SOURCES.forEach((key) => {
      const value = data[key];
      if (value) {
        const stockItem = createStockItem(key, value);
        container.appendChild(stockItem);
      }
    });

    // Duplicate items for seamless marquee loop on mobile
    const items = container.querySelectorAll(".stocks-item");
    items.forEach((item) => {
      const clone = item.cloneNode(true);
      clone.classList.add("stocks-item--clone");
      clone.setAttribute("aria-hidden", "true");
      container.appendChild(clone);
    });

    tickerBar?.classList.remove("stock-ticker--error");
  } catch (error) {
    console.error("Error fetching stock data:", error);
    // Only show error state if we have no content
    if (container.children.length === 0) {
      tickerBar?.classList.add("stock-ticker--error");
    }
  }
}

/**
 * Initialize the stock ticker
 * Finds the ticker container and sets up periodic updates
 */
export function initStockTicker() {
  const tickerContainer = document.querySelector(".stock-ticker__stocks");

  if (!tickerContainer) {
    return;
  }

  // Initial update
  updateTicker(tickerContainer);

  // Update every 5 minutes
  setInterval(() => updateTicker(tickerContainer), UPDATE_INTERVAL);
}
